import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Tabs, 
  Form, 
  Input, 
  Button, 
  Space, 
  message, 
  Modal, 
  Row, 
  Col, 
  Switch,
  Select,
  Alert,
  InputNumber,
  Divider
} from 'antd';
import { 
  SaveOutlined, 
  BugOutlined, 
  HistoryOutlined, 
  ExperimentOutlined,
  InfoCircleOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { RuleSetEditor, ConfigurationPreview } from './';
import { RulingConfigUtils, RULE_OUTCOMES, DEFAULT_RULING_CONFIG } from '../../utils/rulingConfigUtils';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/apiService';

const { TextArea } = Input;
const { Option } = Select;

const RulingConfigurationManager = ({ 
  initialConfiguration = null,
  projectId = null,
  onSave,
  onCancel,
  mode = 'create' // 'create' or 'edit'
}) => {
  const [form] = Form.useForm();
  const [configuration, setConfiguration] = useState(
    initialConfiguration || RulingConfigUtils.createConfigurationTemplate()
  );
  const [activeTab, setActiveTab] = useState('basic');
  const [isTestModalVisible, setIsTestModalVisible] = useState(false);
  const [testData, setTestData] = useState({
    scorecard_1: 0,
    scorecard_2: 0,
    scorecard_3: '',
    scorecard_4: ''
  });
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  
  const { getAuthToken } = useAuth();

  useEffect(() => {
    // Initialize form with configuration data
    form.setFieldsValue({
      name: configuration.name,
      description: configuration.description,
      version: configuration.version,
      is_active: configuration.is_active,
      is_default: configuration.is_default,
      default_outcome: configuration.default_outcome,
      evaluation_order: configuration.evaluation_order
    });
  }, [configuration, form]);

  const handleBasicInfoChange = (changedValues, allValues) => {
    setConfiguration(prev => ({
      ...prev,
      ...allValues
    }));
  };

  const handleRuleSetChange = (ruleName, newRuleSet) => {
    setConfiguration(prev => ({
      ...prev,
      rules: {
        ...prev.rules,
        [ruleName]: newRuleSet
      }
    }));
  };

  const handleEvaluationOrderChange = (newOrder) => {
    setConfiguration(prev => ({
      ...prev,
      evaluation_order: newOrder
    }));
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      
      // Test locally first
      const localResult = RulingConfigUtils.evaluateConfiguration(configuration, testData);
      setTestResult(localResult);
      
      // If API is available, test remotely too for validation
      if (mode === 'edit') {
        try {
          const token = getAuthToken();
          const remoteResult = await apiService.testRulingConfiguration(
            configuration,
            testData,
            token
          );
          
          if (remoteResult.outcome !== localResult) {
            message.warning('Local and remote test results differ - please check configuration');
          }
        } catch (error) {
          console.warn('Remote testing failed, using local result:', error);
        }
      }
      
      message.success(`Test completed: ${localResult.toUpperCase()}`);
    } catch (error) {
      console.error('Test failed:', error);
      message.error('Test failed: ' + error.message);
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Validate configuration
      const validationErrors = RulingConfigUtils.validateConfiguration(configuration);
      if (validationErrors.length > 0) {
        message.error(`Validation failed: ${validationErrors[0]}`);
        return;
      }

      const token = getAuthToken();
      let result;

      if (mode === 'create') {
        result = await apiService.createRulingConfiguration(configuration, token, projectId);
      } else {
        result = await apiService.updateRulingConfiguration(
          configuration._id || configuration.id,
          configuration,
          token,
          projectId
        );
      }

      message.success(`Configuration ${mode === 'create' ? 'created' : 'updated'} successfully!`);
      
      if (onSave) {
        onSave(result);
      }
    } catch (error) {
      console.error('Save failed:', error);
      message.error('Failed to save configuration: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleImportConfiguration = (importedConfig) => {
    setConfiguration(importedConfig);
    form.setFieldsValue({
      name: importedConfig.name,
      description: importedConfig.description,
      version: importedConfig.version,
      is_active: importedConfig.is_active,
      is_default: importedConfig.is_default,
      default_outcome: importedConfig.default_outcome,
      evaluation_order: importedConfig.evaluation_order
    });
    message.success('Configuration imported successfully!');
  };

  const loadDefaultConfiguration = () => {
    Modal.confirm({
      title: 'Load Default Configuration',
      content: 'This will replace your current configuration with the default system configuration. Continue?',
      onOk: () => {
        setConfiguration({
          ...DEFAULT_RULING_CONFIG,
          name: configuration.name || 'New Configuration',
          is_default: false,
          is_active: false
        });
        message.success('Default configuration loaded');
      }
    });
  };

  const validationErrors = RulingConfigUtils.validateConfiguration(configuration);
  const hasErrors = validationErrors.length > 0;

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 16 }}>
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ExperimentOutlined style={{ color: '#8b5cf6' }} />
            <span>{mode === 'create' ? 'Create' : 'Edit'} Ruling Configuration</span>
          </div>
        }
        extra={
          <Space>
            <Button 
              icon={<BugOutlined />}
              onClick={() => setIsTestModalVisible(true)}
            >
              Test
            </Button>
            <Button
              type="default"
              onClick={loadDefaultConfiguration}
            >
              Load Default
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
              disabled={hasErrors}
            >
              {mode === 'create' ? 'Create' : 'Save'}
            </Button>
            {onCancel && (
              <Button onClick={onCancel}>
                Cancel
              </Button>
            )}
          </Space>
        }
      >
        {hasErrors && (
          <Alert
            message="Configuration Validation Errors"
            description={
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            }
            type="error"
            style={{ marginBottom: 16 }}
          />
        )}

        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          tabPosition="top"
          items={[
            {
              label: (
                <span>
                  <InfoCircleOutlined />
                  Basic Information
                </span>
              ),
              key: "basic",
              children: (
                <Form
                  form={form}
                  layout="vertical"
                  onValuesChange={handleBasicInfoChange}
                >
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="name"
                        label="Configuration Name"
                        rules={[
                          { required: true, message: 'Name is required' },
                          { min: 3, message: 'Name must be at least 3 characters' }
                        ]}
                      >
                        <Input placeholder="Enter configuration name" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="version"
                        label="Version"
                        rules={[{ required: true, message: 'Version is required' }]}
                      >
                        <Input placeholder="e.g., 1.0.0" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    name="description"
                    label="Description"
                  >
                    <TextArea
                      rows={3}
                      placeholder="Enter configuration description (optional)"
                    />
                  </Form.Item>

                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item
                        name="is_active"
                        label="Active"
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name="is_default"
                        label="Default Configuration"
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name="default_outcome"
                        label="Default Outcome"
                        rules={[{ required: true, message: 'Default outcome is required' }]}
                      >
                        <Select>
                          <Option value="other">Other</Option>
                          <Option value="pass">Pass</Option>
                          <Option value="refer">Refer</Option>
                          <Option value="fail">Fail</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    name="evaluation_order"
                    label="Evaluation Order"
                    rules={[{ required: true, message: 'Evaluation order is required' }]}
                  >
                    <Select
                      mode="multiple"
                      placeholder="Select rule evaluation order"
                      style={{ width: '100%' }}
                      onChange={handleEvaluationOrderChange}
                    >
                      <Option value={RULE_OUTCOMES.PASS}>Pass</Option>
                      <Option value={RULE_OUTCOMES.REFER}>Refer</Option>
                      <Option value={RULE_OUTCOMES.FAIL}>Fail</Option>
                    </Select>
                  </Form.Item>
                </Form>
              )
            },
            {
              label: (
                <span style={{ color: '#52c41a' }}>
                  ✓ Pass Rules
                </span>
              ),
              key: "pass",
              children: (
                <RuleSetEditor
                  ruleSet={configuration.rules?.pass || RulingConfigUtils.createRuleSetTemplate()}
                  onChange={(newRuleSet) => handleRuleSetChange('pass', newRuleSet)}
                  title="Pass"
                  color="#52c41a"
                />
              )
            },
            {
              label: (
                <span style={{ color: '#faad14' }}>
                  ⚠ Refer Rules
                </span>
              ),
              key: "refer",
              children: (
                <RuleSetEditor
                  ruleSet={configuration.rules?.refer || RulingConfigUtils.createRuleSetTemplate()}
                  onChange={(newRuleSet) => handleRuleSetChange('refer', newRuleSet)}
                  title="Refer"
                  color="#faad14"
                />
              )
            },
            {
              label: (
                <span style={{ color: '#ff4d4f' }}>
                  ✗ Fail Rules
                </span>
              ),
              key: "fail",
              children: (
                <RuleSetEditor
                  ruleSet={configuration.rules?.fail || RulingConfigUtils.createRuleSetTemplate()}
                  onChange={(newRuleSet) => handleRuleSetChange('fail', newRuleSet)}
                  title="Fail"
                  color="#ff4d4f"
                />
              )
            },
            {
              label: (
                <span>
                  <EyeOutlined />
                  Preview & Export
                </span>
              ),
              key: "preview",
              children: (
                <ConfigurationPreview
                  configuration={configuration}
                  onImport={handleImportConfiguration}
                  title="Configuration Preview"
                />
              )
            }
          ]}
        />
      </Card>

      {/* Test Modal */}
      <Modal
        title="Test Configuration"
        open={isTestModalVisible}
        onCancel={() => setIsTestModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsTestModalVisible(false)}>
            Close
          </Button>,
          <Button 
            key="test" 
            type="primary" 
            onClick={handleTest}
            loading={testing}
          >
            Run Test
          </Button>
        ]}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <p>Enter test scorecard values to see how this configuration evaluates them:</p>
        </div>

        <Row gutter={16}>
          <Col span={12}>
            <div style={{ marginBottom: 16 }}>
              <label>Scorecard 1 (Numeric):</label>
              <InputNumber
                style={{ width: '100%' }}
                value={testData.scorecard_1}
                onChange={(value) => setTestData(prev => ({ ...prev, scorecard_1: value }))}
                placeholder="0-10"
                min={0}
                max={10}
                step={0.1}
              />
            </div>
          </Col>
          <Col span={12}>
            <div style={{ marginBottom: 16 }}>
              <label>Scorecard 2 (Numeric):</label>
              <InputNumber
                style={{ width: '100%' }}
                value={testData.scorecard_2}
                onChange={(value) => setTestData(prev => ({ ...prev, scorecard_2: value }))}
                placeholder="0-10"
                min={0}
                max={10}
                step={0.1}
              />
            </div>
          </Col>
          <Col span={12}>
            <div style={{ marginBottom: 16 }}>
              <label>Scorecard 3 (Match-based):</label>
              <Input
                value={testData.scorecard_3}
                onChange={(e) => setTestData(prev => ({ ...prev, scorecard_3: e.target.value }))}
                placeholder="Enter text value"
              />
            </div>
          </Col>
          <Col span={12}>
            <div style={{ marginBottom: 16 }}>
              <label>Scorecard 4 (Match-based):</label>
              <Input
                value={testData.scorecard_4}
                onChange={(e) => setTestData(prev => ({ ...prev, scorecard_4: e.target.value }))}
                placeholder="Enter text value"
              />
            </div>
          </Col>
        </Row>

        {testResult && (
          <div style={{ marginTop: 16, padding: 12, backgroundColor: 'var(--bg-tertiary)', borderRadius: 4 }}>
            <strong>Test Result: </strong>
            <span style={{ 
              color: testResult === 'pass' ? '#52c41a' : 
                     testResult === 'refer' ? '#faad14' : 
                     testResult === 'fail' ? '#ff4d4f' : '#1890ff',
              fontWeight: 'bold',
              textTransform: 'uppercase'
            }}>
              {testResult}
            </span>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default RulingConfigurationManager;